import React from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { getMeta } from 'modules/meta/selectors';
import { getHotelApiRoomsParameters } from 'modules/searchApi/selectors';
import { RootState } from 'store';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Box } from '@daedalus/atlas/helpers/Box';
import { AnalyticsContext, RoomContext } from '@daedalus/core/src/analytics/types/Events';
import { defaultCurrencyCode } from '@daedalus/core/src/localization/business/currency';
import { getCancellationDeadline, getRefundType } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { Offer } from '@daedalus/core/src/offer/types/offer';
import { getDisplayPrice, getTotalUpFrontPrice } from '@daedalus/core/src/price/business/price';
import { updateAllPrices } from '@daedalus/core/src/room/business/transformSapiRoomsResponse/decoratePrices';
import { Room } from '@daedalus/core/src/room/types/room';
import { RoundingStrategyVariants } from '@daedalus/core/src/utils/number';
import { OfferDetails } from '@daedalus/shared/src/booking/OfferDetails';
import { getFrozenBookingsByRoomIds } from '@daedalus/shared/src/dealFreeze/modules/selector';
import { useHandlePriceDetailsClick } from '../RoomOffer/useHandlePriceDetailsClick';
import { RoomOfferPrice } from '../RoomOfferPrice/RoomOfferPrice';
import { FrozenRoomOfferCTA } from './FrozenRoomOfferCTA';
import { FrozenUntilBanner } from './FrozenUntilBanner';
interface Props {
  hotelId: string;
  room: Room;
  roomContext: RoomContext;
}
const FrozenUntilBannerDesktopWrapper = /*#__PURE__*/styled('div')({
  name: "FrozenUntilBannerDesktopWrapper",
  class: "f3b84kl",
  propsAsIs: false
});
export const FrozenRoomOffer = ({
  hotelId,
  room,
  roomContext
}: Props) => {
  const {
    isMobile
  } = useDeviceLayout();
  const {
    includeTaxes,
    includeLocalTaxes,
    currencyCode
  } = useSelector(getMeta);
  const {
    checkIn,
    checkOut,
    rooms
  } = useSelector(state => getHotelApiRoomsParameters(state, hotelId));
  const [frozenBooking] = useSelector((state: RootState) => getFrozenBookingsByRoomIds(state, [room.id, ...(room.squashedIds || [])]));
  const {
    prices = [],
    cancellationPenalties = []
  } = frozenBooking || {};
  const [frozenRoom] = frozenBooking.rooms || [];

  // Parse the prices to be comprehensible by the RoomOfferPrice component
  const params = {
    checkIn,
    checkOut,
    rooms,
    hotelId
  };
  const parsedPrices = prices.map(price => updateAllPrices(price, params, {
    includeTaxes,
    includeLocalTaxes
  }));
  const frozenOffer = {
    cancellationPenalties,
    prices: parsedPrices
  } as Offer;
  const handlePriceDetailsClick = useHandlePriceDetailsClick({
    hotelId,
    room,
    offer: frozenOffer,
    component: 'FrozenRoomOffer',
    analyticsContext: {
      [AnalyticsContext.RoomContext]: roomContext
    }
  });

  // Offer details
  const cancellationDeadline = getCancellationDeadline(cancellationPenalties);
  const refundType = getRefundType(cancellationPenalties);
  const displayPrice = getDisplayPrice(frozenOffer.prices);
  const totalChargeablePrice = getTotalUpFrontPrice(displayPrice);
  return <>
      {!isMobile && <FrozenUntilBannerDesktopWrapper>
          <FrozenUntilBanner expiresAt={frozenBooking.expiresAt} />
        </FrozenUntilBannerDesktopWrapper>}

      <Box display="flex" justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} gap={isMobile ? 's250' : 's600'}>
        <OfferDetails boardType={frozenRoom.boardType} refundType={refundType} freeCancellationDeadline={cancellationDeadline} cancellationPenalties={cancellationPenalties} totalPrice={totalChargeablePrice} currencyCode={currencyCode || defaultCurrencyCode} hideFreeCancellationDeadlineDate prefersReducedMotion />

        <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection="row" gap="s600">
          <RoomOfferPrice isPrivate hotelId={hotelId} offer={frozenOffer} onClickPriceDetails={handlePriceDetailsClick} roundingStrategy={RoundingStrategyVariants.trunc} />

          {!isMobile && <FrozenRoomOfferCTA bookingId={frozenBooking.id} />}
        </Box>
      </Box>

      {isMobile && <>
          <FrozenUntilBanner expiresAt={frozenBooking.expiresAt} />
          <FrozenRoomOfferCTA bookingId={frozenBooking.id} />
        </>}
    </>;
};

require("./FrozenRoomOffer.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./FrozenRoomOffer.tsx");